import ApiInstance from "./ApiInstance";
import { downloadCsv } from "../util/downloadCsv";

// Anonymous organisation barometer v2 — modular waves:
//   welzijn      = Cantril-ladder (2 items, 0..10)
//   burnout      = BAT-12 (12 items, 1..5)
//   werkbeleving = eigen Carefunded-werkitems (7 items, 1..5 — geen gevalideerd
//                  klinisch instrument; de UI labelt dat expliciet)
// Respondents are anonymous by design: one shared wave link, no login, and the
// server stores nothing about the requester. Individual respondents never get
// a score back (MDR: uniform signpost only); employers only see k>=10
// aggregates, per module.

export type BarometerModule = "welzijn" | "burnout" | "werkbeleving";
export const BAROMETER_MODULES: BarometerModule[] = ["welzijn", "burnout", "werkbeleving"];

export interface BarometerWaveSummary {
  id: string;
  title: string;
  modules: BarometerModule[];
  status: "open" | "closed";
  shareToken: string;
  openedAt: string;
  closedAt: string | null;
}

export interface BarometerMeta {
  orgName: string;
  title: string;
  status: "open" | "closed";
  kThreshold: number;
  modules: BarometerModule[];
}

export interface BarometerAnswers {
  cantril?: number[];
  bat12?: number[];
  werk?: number[];
}

export interface BarometerWelzijnAgg {
  n: number;
  itemMeans: number[]; // [nu, over-5-jaar]
  nowMean: number;
  futureMean: number;
}
export interface BarometerBurnoutAgg {
  n: number;
  subscales: Record<string, number>;
  overallMean: number;
}
export interface BarometerWerkAgg {
  n: number;
  itemMeans: number[];
  overallMean: number;
}

export interface BarometerPrevious {
  id: string;
  title: string;
  welzijn?: BarometerWelzijnAgg;
  burnout?: BarometerBurnoutAgg;
  werkbeleving?: BarometerWerkAgg;
}

export interface BarometerReport {
  id: string;
  title: string;
  status: "open" | "closed";
  modules: BarometerModule[];
  kThreshold: number;
  suppressed: boolean;
  responseCount?: null; // only present (and always null) when suppressed
  // Per module: absent when that module is below k (no count leaks).
  welzijn?: BarometerWelzijnAgg;
  burnout?: BarometerBurnoutAgg;
  werkbeleving?: BarometerWerkAgg;
  previous?: BarometerPrevious | null;
}

class BarometerApi {
  // Public (no auth) — wave meta by share token.
  async meta(token: string): Promise<BarometerMeta> {
    const { data } = await ApiInstance.getApi().get(`/barometer/respond/${token}`);
    return data;
  }
  // Public (no auth) — submit an anonymous response (only the wave's modules).
  async respond(token: string, answers: BarometerAnswers): Promise<void> {
    await ApiInstance.getApi().post(`/barometer/respond/${token}`, answers);
  }

  // Org admin.
  async listWaves(): Promise<BarometerWaveSummary[]> {
    const { data } = await ApiInstance.getApi().get("/barometer/waves");
    return data.waves;
  }
  async createWave(title: string, modules: BarometerModule[]): Promise<BarometerWaveSummary> {
    const { data } = await ApiInstance.getApi().post("/barometer/waves", { title, modules });
    return data;
  }
  async closeWave(id: string): Promise<void> {
    await ApiInstance.getApi().post(`/barometer/waves/${id}/close`, {});
  }
  async report(id: string): Promise<BarometerReport> {
    const { data } = await ApiInstance.getApi().get(`/barometer/waves/${id}/report`);
    return data;
  }
  // Same k-safe aggregate as the report, as a CSV download (manual §3.3).
  async downloadReportCsv(id: string): Promise<void> {
    await downloadCsv(`/barometer/waves/${id}/report.csv`, "barometer-golf.csv");
  }
}

const barometerApi = new BarometerApi();
export default barometerApi;
